import { Component, OnInit } from '@angular/core';
import { UsageAmountsService } from '../services/usage/usage-amounts.service';
import { DataPoint, GraphPair, GraphType, PlatformMetricsMonthly, TenantMetricBreakdown } from '../app.types';
import { convert, convertDateToMonth } from 'src/util/conversion-helpers';

interface RangeOption {
  label: string;
  months: number;
  /** When set, show only the in-progress current month; otherwise the N completed months before it. */
  currentOnly?: boolean;
}

interface MetricChart {
  id: string;
  title: string;
  domainMonths: number;
  graphPair: GraphPair;
}

interface BreakdownRow {
  tenant: string;
  value: number;
  display: string;
}

/** A KPI metric that can be drilled into per tenant. */
interface BreakdownMetric {
  title: string;
  value: (t: TenantMetricBreakdown) => number;
  format: (n: number) => string;
}

/**
 * Synectics-internal, cross-tenant platform metrics. Shows platform-wide monthly totals for a
 * selectable window (Synx-gated endpoint over already-aggregated blob + Usage DB data), with charts
 * styled via the shared x-y-graph component. Each usage KPI can be clicked to drill into a per-tenant
 * breakdown for the selected range.
 */
@Component({
    selector: 'metrics-dashboard',
    templateUrl: './metrics-dashboard.component.html',
    styleUrls: ['./metrics-dashboard.component.scss'],
    standalone: false
})
export class MetricsDashboardComponent implements OnInit {
  readonly ranges: RangeOption[] = [
    { label: 'Current month', months: 1, currentOnly: true },
    { label: 'Last month', months: 1 },
    { label: 'Last 3 months', months: 3 },
    { label: 'Last 6 months', months: 6 },
    { label: 'Last 12 months', months: 12 },
  ];
  selectedRange: RangeOption = this.ranges[this.ranges.length - 1]; // default: Last 12 months

  isLoading = false;
  data: PlatformMetricsMonthly[] = [];

  currentMonthHint = '';

  // Headline figures (range totals; storage is a point-in-time value so we show the latest month).
  incidentsTotal = 0;
  currentStorageGb = 0;
  videoMinutes = 0;
  redactionMinutes = 0;
  clipDataGb = 0;
  clipsAddedTotal = 0;
  uniqueUsers = 0;

  // Featured, full-width chart plus the secondary grid.
  storageChart: MetricChart | null = null;
  charts: MetricChart[] = [];

  // Per-tenant drill-down (opened by clicking a KPI tile).
  breakdownOpen = false;
  breakdownLoading = false;
  breakdownTitle = '';
  breakdownRows: BreakdownRow[] = [];
  private breakdownMetricKey = '';
  private breakdownCache: TenantMetricBreakdown[] | null = null;

  // Guards against an earlier request's response overwriting a later selection's data.
  private requestSeq = 0;

  private static readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  private static readonly hues = {
    storage: '#439FD3',
    storageAdded: '#39D6D6',
    incidents: '#B77FFF',
    video: '#14AE5C',
    redaction: '#E0A32E',
    clipData: '#F2779A',
    clips: '#8C9EFF',
    users: '#5B6BF5',
  };

  // Drives which KPI tiles are clickable and how each tenant value is read/formatted in the drill-down.
  private readonly breakdownMetrics: Record<string, BreakdownMetric> = {
    incidents: { title: 'Incidents Created', value: (t) => t.incidentsCreated, format: (n) => n.toLocaleString() },
    uniqueUsers: { title: 'Unique Users', value: (t) => t.uniqueUsers, format: (n) => n.toLocaleString() },
    video: { title: 'Video Streamed (mins)', value: (t) => Math.round(t.videoMinutesStreamed), format: (n) => n.toLocaleString() },
    redaction: { title: 'Redaction (mins)', value: (t) => Math.round(t.redactionSeconds / 60), format: (n) => n.toLocaleString() },
    clipData: { title: 'Clip Data Added (GB)', value: (t) => this.toGb(t.clipDataAddedBytes), format: (n) => `${n.toLocaleString()} GB` },
    clipsAdded: { title: 'Clips Added', value: (t) => t.clipsAdded, format: (n) => n.toLocaleString() },
  };

  constructor(private usageService: UsageAmountsService) {}

  ngOnInit(): void {
    // Build empty chart shells up front so the grid is laid out before data arrives; otherwise the
    // charts are created (by *ngIf) only once data lands and x-y-graph measures a zero-width cell.
    this.buildView();
    this.load();
  }

  /** Subtext shown on the KPI tiles, matching the selected range (e.g. "Last 3 months"). */
  get rangeLabel(): string {
    return this.selectedRange.label;
  }

  selectRange(range: RangeOption): void {
    if (range === this.selectedRange) {
      return;
    }
    this.selectedRange = range;
    this.load();
  }

  private load(): void {
    const seq = ++this.requestSeq;
    this.isLoading = true;
    this.breakdownCache = null; // range changed - drill-down must be re-fetched
    const range = this.selectedRange;
    this.usageService.getPlatformMetrics(range.months, !!range.currentOnly).subscribe((res) => {
      if (seq !== this.requestSeq) {
        return; // a newer selection has superseded this response
      }
      this.data = res && Array.isArray(res.months) ? res.months : [];
      this.uniqueUsers = res?.uniqueUsersInRange ?? 0;
      this.buildView();
      this.isLoading = false;
      // x-y-graph measures its container width when created; the charts are revealed as data lands,
      // before layout settles, so nudge a re-measure once the browser has laid them out.
      setTimeout(() => window.dispatchEvent(new Event('resize')));
    });
  }

  private buildView(): void {
    const now = new Date();
    const hasCurrentMonth = this.data.some((d) => d.year === now.getFullYear() && d.month === now.getMonth() + 1);
    this.currentMonthHint = hasCurrentMonth
      ? `${MetricsDashboardComponent.monthNames[now.getMonth()]} ${now.getFullYear()} is the current month and still in progress.`
      : '';

    const sum = (selector: (d: PlatformMetricsMonthly) => number) =>
      this.data.reduce((acc, d) => acc + (selector(d) || 0), 0);

    const latestTotalStorage = this.data.length ? this.data[this.data.length - 1].totalStorageBytes : 0;

    this.incidentsTotal = sum((d) => d.incidentsCreated);
    this.currentStorageGb = this.toGb(latestTotalStorage);
    this.videoMinutes = Math.round(sum((d) => d.videoMinutesStreamed));
    this.redactionMinutes = Math.round(sum((d) => d.redactionSeconds) / 60);
    this.clipDataGb = this.toGb(sum((d) => d.clipDataAddedBytes));
    this.clipsAddedTotal = sum((d) => d.clipsAdded);
    // uniqueUsers is set from the response (deduplicated across the range), not summed here.

    const hues = MetricsDashboardComponent.hues;
    const domainMonths = this.data.length || 1;
    const startDate = this.data.length
      ? new Date(this.data[0].year, this.data[0].month - 1, 1)
      : new Date();

    const chart = (id: string, title: string, type: GraphType, color: string,
      selector: (d: PlatformMetricsMonthly) => number): MetricChart => ({
        id,
        title,
        domainMonths,
        graphPair: {
          leftGraph: { description: title, type, color, data: this.points(selector), labelType: 'normal' },
          rightGraph: { description: null, type: 'line', color: '#39D6D6', data: [], labelType: 'normal' },
          startDate,
        },
      });

    // Total Storage is a line for a multi-month trend, but a lone point looks like a stray dot, so a
    // single-month window renders it as a bar like the other single-month charts.
    const storageType: GraphType = this.data.length <= 1 ? 'bar' : 'line';
    this.storageChart = chart('storageTotal', 'Total Storage (GB)', storageType, hues.storage, (d) => this.toGb(d.totalStorageBytes));

    this.charts = [
      chart('incidents', 'Incidents Created', 'bar', hues.incidents, (d) => d.incidentsCreated),
      chart('uniqueUsers', 'Unique Users per Month', 'bar', hues.users, (d) => d.uniqueLogins),
      chart('video', 'Video Streamed (mins)', 'bar', hues.video, (d) => d.videoMinutesStreamed),
      chart('redaction', 'Redaction (mins)', 'bar', hues.redaction, (d) => d.redactionSeconds / 60),
      chart('clipsAdded', 'Clips Added', 'bar', hues.clips, (d) => d.clipsAdded),
      chart('storageAdded', 'Storage Added (GB)', 'bar', hues.storageAdded, (d) => this.toGb(d.storageAddedBytes)),
      chart('clipData', 'Clip Data Added (GB)', 'bar', hues.clipData, (d) => this.toGb(d.clipDataAddedBytes)),
    ];
  }

  /** Opens the per-tenant drill-down for a KPI, fetching the breakdown for the current range once. */
  openBreakdown(metricKey: string): void {
    if (!this.breakdownMetrics[metricKey]) {
      return;
    }
    this.breakdownMetricKey = metricKey;
    this.breakdownTitle = this.breakdownMetrics[metricKey].title;
    this.breakdownOpen = true;

    if (this.breakdownCache) {
      this.applyBreakdown();
      return;
    }

    this.breakdownLoading = true;
    const range = this.selectedRange;
    this.usageService.getTenantBreakdown(range.months, !!range.currentOnly).subscribe((rows) => {
      this.breakdownCache = Array.isArray(rows) ? rows : [];
      this.breakdownLoading = false;
      this.applyBreakdown();
    });
  }

  closeBreakdown(): void {
    this.breakdownOpen = false;
  }

  private applyBreakdown(): void {
    const metric = this.breakdownMetrics[this.breakdownMetricKey];
    this.breakdownRows = (this.breakdownCache || [])
      .map((t) => {
        const value = metric.value(t);
        return { tenant: t.tenantName, value, display: metric.format(value) };
      })
      .filter((row) => row.value > 0)
      .sort((a, b) => b.value - a.value);
  }

  private points(selector: (d: PlatformMetricsMonthly) => number): DataPoint[] {
    // x-y-graph matches data points to its month domain by name, so use the same label helper.
    return this.data.map((d) => ({
      name: convertDateToMonth(new Date(d.year, d.month - 1, 1)),
      actual: this.round(selector(d) || 0),
    }));
  }

  private toGb(bytes: number): number {
    return this.round(convert('bytes', 'gigabytes', bytes || 0));
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
